// A settings row's picture: what shows now, "Choose image" (a file, then the
// cropper) and "Remove". The same for every picture that stands for someone
// or something (the account's, a workspace's own avatar, a workspace's icon).
//
// Promise: the chosen square is uploaded here and handed to `onPicked` as a
// file id (or, with `save`, handed over as it is); the dialog stays until that
// settles. A failure to remove shows
// under the row's buttons; one to save, under the dialog's.
import { m } from "@trameria/i18n";
import { type ReactNode, useRef, useState } from "react";
import { useApi } from "../app/context";
import { apiErrorMessage } from "../data/errors";
import { uploadFile } from "../documents/blocks/upload";
import { ErrorLine } from "./bits";
import { Button } from "./button";
import { PictureCropModal } from "./picture-crop";

const EXT: Record<string, string> = {
  "image/webp": "webp",
  "image/jpeg": "jpg",
  "image/png": "png",
};

export function PictureField({
  preview,
  label,
  removable,
  removeLabel = m.common_picture_remove(),
  onPicked,
  save,
  onRemove,
}: {
  /** The picture as it shows now. */
  preview: ReactNode;
  /** What the picture is (the cropper's title and the button's name aloud). */
  label: string;
  /** Whether there is one of its own to take away. */
  removable: boolean;
  /** "Remove", or what removing does where something else then shows. */
  removeLabel?: string;
  /** Takes the file uploaded here. */
  onPicked?(fileId: string): Promise<unknown>;
  /** Takes the cropped image itself instead (it is kept elsewhere). */
  save?(picture: Blob): Promise<unknown>;
  onRemove(): Promise<unknown>;
}) {
  const api = useApi();
  const input = useRef<HTMLInputElement>(null);
  const [file, setFile] = useState<File | null>(null);
  const [removing, setRemoving] = useState(false);
  const [failed, setFailed] = useState<string | null>(null);

  const store = async (picture: Blob) => {
    if (save) {
      await Promise.resolve(save(picture)).catch((error: unknown) => {
        throw new Error(apiErrorMessage(error, m.common_picture_failed()));
      });
      setFile(null);
      return;
    }
    const ext = EXT[picture.type] ?? "png";
    const uploaded = await uploadFile(
      api,
      new File([picture], `picture.${ext}`, { type: picture.type }),
    ).promise.catch((error: unknown) => {
      throw new Error(apiErrorMessage(error, m.common_picture_failed()));
    });
    await Promise.resolve(onPicked?.(uploaded.id)).catch((error: unknown) => {
      throw new Error(apiErrorMessage(error, m.common_picture_failed()));
    });
    setFile(null);
  };

  const remove = async () => {
    setFailed(null);
    setRemoving(true);
    try {
      await onRemove();
    } catch (error) {
      setFailed(apiErrorMessage(error, m.common_picture_failed()));
    } finally {
      setRemoving(false);
    }
  };

  return (
    <div className="flex w-full flex-col items-end">
      <div className="flex items-center gap-2">
        {preview}
        <Button
          aria-label={`${label}: ${m.common_picture_choose()}`}
          onClick={() => input.current?.click()}
        >
          {m.common_picture_choose()}
        </Button>
        {removable && (
          <Button
            variant="ghost"
            disabled={removing}
            aria-busy={removing}
            onClick={() => void remove()}
          >
            {removeLabel}
          </Button>
        )}
        <input
          ref={input}
          type="file"
          accept="image/png,image/jpeg,image/gif,image/webp"
          hidden
          aria-label={label}
          onChange={(e) => {
            const chosen = e.target.files?.[0];
            e.target.value = "";
            if (chosen) {
              setFailed(null);
              setFile(chosen);
            }
          }}
        />
      </div>
      {failed && <ErrorLine>{failed}</ErrorLine>}
      <PictureCropModal
        file={file}
        label={label}
        onCancel={() => setFile(null)}
        onDone={store}
      />
    </div>
  );
}
